/**
 * 제품 복사 규칙 (figma 1498:51046 "제픔 복사").
 *
 * 경로는 둘이지만 복사 규칙 자체는 하나다. 품목코드·바코드 처리만 갈린다.
 *  - 다건(bulk):   품목코드·바코드를 공란으로 만들어 바로 생성한다.
 *                 (figma 1498:51019 "복사본의 품목 코드·바코드는 빈칸으로 생성되며")
 *  - 단건(single): 원본 값을 그대로 프리필하고 상세화면에서 새 값을 받는다.
 *                 (figma 1495:45007 — 프리필된 두 필드에 빨간 테두리 + 안내)
 *
 * 서버 계약 (sagas/index.js `_createOrUpdateProduct`):
 *   `product.itemId` 가 없을 때만 API.createItem 으로 신규 생성한다.
 *   → id / itemId / sku.id / sku.itemId 를 반드시 지워야 원본 update 로 새지 않는다.
 *
 * 서버 계약 (ExcelUpload.js:80 안내 문구):
 *   바코드라벨·품목코드(SKU)는 중복 생성이 거부된다. 그래서 다건은 공란으로,
 *   단건은 저장 전 중복 해소를 강제한다.
 */
import _ from "lodash";

/** 복사본 제품명 접두 (figma 1495:48744 "(복사됨)카피치노", 1495:50107 "(복사됨)아이스 아메리카노") */
export const COPY_NAME_PREFIX = "(복사됨)";

/** 복사 프리필된 품목코드·바코드를 표시하는 임시 필드. 저장 직전 제거된다 (sku.codeGen 과 동일 취급). */
export const COPIED_CODE_KEY = "copiedCode";

/**
 * 제품 레벨에서 복사본이 물려받으면 안 되는 필드.
 *  - id/itemId/createdAt/updatedAt: 신규 생성 판별 및 서버 타임스탬프
 *  - origin: 제품을 만든 지점 — 신규 생성과 동일하게 서버가 새로 정한다
 *  - refer/referId: 외부 채널 연동 참조. 시안 문구상 복사본은 미연동으로 생성된다
 *  - storeId: saga 가 현재 매장으로 다시 채운다
 *  - categories/modifiers/classifications: LS_getProductsByIds 의 connection 형태.
 *    createItem 입력은 categoryIds/modifierIds 평탄화 배열을 받는다
 *  - internalName/note: 내부 관리용 이름·노트는 복사 대상이 아니다 (기획 확정)
 *
 * classifications 는 미사용 기능이라 승계하지 않는다. 분류 승계는 categoryIds 만.
 *
 * suspect: brandIds 는 getProduct / LS_getProductsByIds 두 쿼리 모두 select 하지 않는다.
 *   → 기존 "제품 수정" 진입도 브랜드를 못 읽는 상태라 복사본도 동일하게 브랜드가 비게 된다.
 *   쿼리 파일은 서버 관리 대상이라 여기서 고칠 수 없음 (서버 필드 추가 대기).
 */
const PRODUCT_OMIT_KEYS = [
  "id",
  "itemId",
  "createdAt",
  "updatedAt",
  "origin",
  "refer",
  "referId",
  "storeId",
  "categories",
  "modifiers",
  "classifications",
  "internalName",
  "note",
];

/**
 * SKU 레벨에서 복사본이 물려받으면 안 되는 필드.
 *  - id/itemId: 신규 SKU 로 채번되어야 한다
 *  - inventories/stock/quantity: 실재고. 수량만 0으로 두고 설정값(stockTrack/stockAlert*)은 승계
 *  - links: 외부 채널 연동 링크. 복사본은 미연동 상태
 *  - refer/referId: 위와 동일
 *  - locationId: 원본 SKU 를 만든 지점. 신규 SKU 에는 없는 값이라 승계하지 않는다
 *  - isActive: 폐기 필드 — product.available 로 통합됐다(ProductEditor.js INIT_SKU 주석).
 *    신규 SKU 는 이 값을 안 보내므로 복사본도 보내지 않는다
 *  - createdAt/updatedAt: 서버 타임스탬프
 *
 * 반대로 stockTrack / stockAlert / stockAlertQty / stockControl / limitQuantity /
 * unitSuffix / locations(지점별 override) / attributes 는 "설정값"이라 그대로 승계한다.
 */
const SKU_OMIT_KEYS = [
  "id",
  "itemId",
  "inventories",
  "stock",
  "links",
  "refer",
  "referId",
  "locationId",
  "isActive",
  "createdAt",
  "updatedAt",
];

/** 이름 앞에 "(복사됨)" 을 붙인다. 단건·다건 모두 붙이고 중첩("(복사됨)(복사됨)X")도 허용. */
export const withCopyPrefix = (name) =>
  `${COPY_NAME_PREFIX}${name == null ? "" : String(name)}`;

/**
 * LS_getProductsByIds 응답을 ProductEditor / createItem 이 쓰는 평탄한 형태로 정규화한다.
 * (Product.js 의 buildProduct 가 redux 조각으로 하는 일을 서버 응답 하나로 처리)
 *
 * @param {object} raw - LS_getProductsByIds 항목
 * @returns {object} categoryIds / modifierIds 가 배열로 채워진 제품 객체
 */
export const normalizeCopySource = (raw) => {
  if (!raw) return null;
  const source = _.cloneDeep(raw);
  // categories: { items: [{ categoryId, ... }] }
  if (!Array.isArray(source.categoryIds)) {
    source.categoryIds = (source.categories?.items || [])
      .map((cp) => cp?.categoryId)
      .filter(Boolean);
  }
  // modifiers: { items: [{ modifierId, position, ... }] } — position 있으면 그 순서를 보존
  if (!Array.isArray(source.modifierIds)) {
    const items = (source.modifiers?.items || []).filter(Boolean);
    const hasPosition = items.some((pm) => pm.position != null);
    source.modifierIds = (
      hasPosition ? _.sortBy(items, (pm) => pm.position ?? 0) : items
    )
      .map((pm) => pm.modifierId)
      .filter(Boolean);
  }
  return source;
};

/**
 * 원본 제품 → 복사본 payload.
 *
 * @param {object} source - normalizeCopySource 를 거친 원본 제품
 * @param {object} options
 * @param {boolean} [options.bulk=false]          - true 면 다건(품목코드·바코드 공란), false 면 단건(프리필)
 * @param {boolean} [options.keepAvailable=true]  - 확인 팝업 "제품을 판매 중 상태로 복사하기" 체크값
 * @param {boolean} [options.showSkuMgmt=true]    - 품목 코드 입력 UI 노출 여부 (itemConfig.skuMgmt || isRetail)
 * @param {boolean} [options.showBarcodeMgmt=true]- 바코드 라벨 입력 UI 노출 여부 (itemConfig.barcodeMgmt || isRetail)
 * @returns {object} createItem / ProductEditor 에 그대로 넣을 수 있는 제품 객체
 */
export const buildCopiedProduct = (
  source,
  {
    bulk = false,
    keepAvailable = true,
    showSkuMgmt = true,
    showBarcodeMgmt = true,
  } = {},
) => {
  if (!source) return null;
  const src = normalizeCopySource(source);
  const copied = _.omit(_.cloneDeep(src), PRODUCT_OMIT_KEYS);

  copied.type = "product";
  copied.name = withCopyPrefix(src.name);
  // 확인 팝업 체크박스(figma 1498:51019): ON(기본)이면 판매 중, OFF 면 품절 상태로 생성
  copied.available = keepAvailable !== false;

  copied.skus = (src.skus || []).map((rawSku) => {
    const sku = _.omit(_.cloneDeep(rawSku), SKU_OMIT_KEYS);
    // 재고 "수량"만 초기화. 재고추적/안전재고 등 설정값은 승계
    sku.quantity = null;

    // 품목 코드 UI 자체가 꺼진 계정(외식업 + skuMgmt off)에서는 프리필해도 고칠 화면이 없다 → 공란.
    const canFixModel = showSkuMgmt;
    const canFixBarcode = showBarcodeMgmt;
    const model = bulk || !canFixModel ? null : rawSku.model || null;
    // 바코드는 legacy 호환으로 unit 에도 같은 값이 들어간다 (ProductEditor.js:2176-2179)
    const barcode =
      bulk || !canFixBarcode ? null : rawSku.barcode || rawSku.unit || null;

    sku.model = model;
    sku.barcode = barcode;
    sku.unit = barcode;

    // 단건 복사에서 실제로 프리필된 값만 "해소해야 할 중복"으로 표시한다.
    const copiedCode = {};
    if (model) copiedCode.model = model;
    if (barcode) copiedCode.barcode = barcode;
    if (Object.keys(copiedCode).length > 0) sku[COPIED_CODE_KEY] = copiedCode;

    return sku;
  });

  return copied;
};

/**
 * 프리필된 복사 값이 아직 그대로인지 판정한다.
 * 사용자가 값을 바꾸면(또는 자동 생성을 누르면) 즉시 false 가 되어 에러가 풀린다.
 *
 * @param {object} sku
 * @param {"model"|"barcode"} key
 */
export const isUnresolvedCopiedCode = (sku, key) => {
  const copied = sku?.[COPIED_CODE_KEY];
  if (!copied || copied[key] == null) return false;
  const current = key === "barcode" ? sku.barcode || sku.unit : sku[key];
  return String(current || "") === String(copied[key]);
};
